import React from 'react';
import PropTypes from 'prop-types';
import ArticleCardDetailed from './ArticleCardDetailed';
import ArticleCardList from './ArticleCardList';
import { articleCardPropTypes, articleCardDefaultProps } from './ArticleCardProps';
import { isQualitativeAnalysisAiApplied } from '../../../utils/aiAnalysisStatus';

/**
 * ArticleCard - Componente organismo que seleciona a variante apropriada
 *
 * Este componente atua como um Factory que retorna a implementação
 * correta com base na variante selecionada.
 *
 * TRANSFORMER PATTERN:
 * - Uma única interface de props compartilhada entre todas as variantes
 * - Adicionar nova prop = modificar apenas ArticleCardProps.js
 * - Todas as variantes automaticamente recebem a nova prop
 * - Comportamento comum extraído para useCardBehavior hook
 *
 * Variantes disponíveis:
 * - detailed: Card grande com imagem, hover overlays e action buttons
 * - list: Card em linha horizontal, sem hover overlays (actions inline)
 *
 * Como adicionar nova funcionalidade:
 * 1. Adicionar prop em ArticleCardProps.js (única modificação necessária)
 * 2. Se for ação/comportamento comum, adicionar em useCardBehavior.js
 * 3. Todas as variantes recebem automaticamente
 */
const ArticleCard = ({
  article,
  variant = 'detailed',
  onRemoveTag,
  ...props
}) => {
  const { renderTags, ...cardProps } = props;

  // Seleciona o componente adequado com base na variante
  switch (variant) {
    case 'list':
      return <ArticleCardList article={article} onTagRemove={onRemoveTag} {...cardProps} />;
    case 'detailed':
    default:
      return <ArticleCardDetailed article={article} onTagRemove={onRemoveTag} {...cardProps} />;
  }
};

// Usa a interface compartilhada - única fonte da verdade
ArticleCard.propTypes = {
  ...articleCardPropTypes,
  variant: PropTypes.oneOf(['detailed', 'list'])
};

ArticleCard.defaultProps = {
  ...articleCardDefaultProps,
  variant: 'detailed'
};

// Função de comparação customizada para React.memo
// Só re-renderiza se as props críticas mudarem
const areEqual = (prevProps, nextProps) => {
  // Se o artigo mudou de ID, sempre re-renderizar
  if (prevProps.article._id !== nextProps.article._id) {
    return false;
  }
  
  // Se o estado de seleção mudou, re-renderizar
  if (prevProps.isSelected !== nextProps.isSelected) {
    return false;
  }
  
  // Se o modo de seleção mudou, re-renderizar
  if (prevProps.selectionMode !== nextProps.selectionMode) {
    return false;
  }
  
  // Se a variante mudou, re-renderizar
  if (prevProps.variant !== nextProps.variant) {
    return false;
  }

  if (isQualitativeAnalysisAiApplied(prevProps.article) !== isQualitativeAnalysisAiApplied(nextProps.article)) {
    return false;
  }
  
  // Verificar se as propriedades do artigo que aparecem no card mudaram
  const articlePropsToCheck = [
    'isFavorite', 'isRead', 'sentiment', 'title', 'image', 
    'siteName', 'date', 'valuation', 'likesNumber', 'viewsNumber'
  ];
  
  for (const prop of articlePropsToCheck) {
    if (prevProps.article[prop] !== nextProps.article[prop]) {
      return false;
    }
  }
  
  // Verificar se as tags mudaram (comparação superficial)
  if (prevProps.article.tags?.length !== nextProps.article.tags?.length) {
    return false;
  }
  
  // Não re-renderizar se apenas callbacks mudaram (evita re-renders por causa de inline functions)
  return true;
};

// Memoizar o factory também para evitar re-renders desnecessários
export default React.memo(ArticleCard, areEqual);
